import { type CSSProperties, useCallback } from 'react';

import SearchSelectModal from '@/components/Select/SearchSelectModal';
import type { TriggerPos } from '@/components/Select/types';
import { MainText } from '@/components/Typography/NamedTypo';
import { COMPANY_PAGE_SIZE } from '@/features/Account/store';
import service from '@/services/index';
import useWorkspaceStore from '@/stores/WorkspaceStore';
import { getPaginationQueries } from '@/utils/pagination';
import { buildQuery } from '@/utils/util';

type Props = {
  name: string;
  style?: CSSProperties;
  createNewCompany?: (alias: string) => Promise<CompanyT>;
  toggleItem: (company: PaginatedData<CompanyT> | null) => void;
  triggerEl?: HTMLDivElement | null;
  clearable: boolean;
  defaultQuery?: Record<string, string>;
  triggerPos?: TriggerPos;
};

const CompanySelectModal = (props: Props) => {
  const currWorkspace = useWorkspaceStore((state) => state.currWorkspace);

  const fetchData = useCallback(
    async (
      prev?: PaginatedData<CompanyT>[],
      currPage?: number,
      targetPage?: number,
      searchValue?: string,
      accumulate?: boolean,
    ) => {
      if (!currWorkspace?.alias) throw new Error('Workspace not found');
      const queries = getPaginationQueries({
        queries: {
          account_name: searchValue || null,
          ...(props.defaultQuery || {}),
        },
        order: 'asc',
        prevData: prev || [],
        currPage: currPage || 0,
        targetPage: targetPage || 0,
        sortby: 'name',
        cursorKey: 'paginate_token',
        acc: accumulate,
      });

      const queryString = buildQuery(queries);

      const res = await service.account.fetchCompanyList(queryString);
      return {
        list: res,
        page: targetPage || 0,
        hasNextPage: !!res[COMPANY_PAGE_SIZE - 1],
      };
    },
    [currWorkspace?.alias, props.defaultQuery],
  );

  return (
    <SearchSelectModal
      name={props.name}
      modalWidth="350px"
      triggerEl={props.triggerEl}
      triggerPos={props.triggerPos}
      placeholder="Search existing or add new..."
      createNewItem={props.createNewCompany}
      toggleItem={props.toggleItem}
      dataList={[]}
      fetchFn={fetchData}
      async
      clearable={props.clearable}
      clearText={
        <MainText dim style={{ padding: '8px 16px' }}>
          Not Assigned
        </MainText>
      }
      onClear={() => props.toggleItem(null)}
      parser={(companyList) =>
        companyList.map((company) => ({
          alias: company.alias,
          name: company.name,
          node: company.name,
        }))
      }
    />
  );
};

export default CompanySelectModal;
